import { Download } from 'lucide-react';
import { useEffect, useState } from 'react';
import { PermissionGate } from '../../../../auth/PermissionGate';
import { Button } from '../../../../components/Button';
import { ErrorState } from '../../../../components/ErrorState';
import { Modal } from '../../../../components/Modal';
import { PreviewTable } from '../../../../components/PreviewTable';
import { RichTextDocument } from '../../../../components/RichTextDocument';
import { Skeleton } from '../../../../components/Skeleton';
import { useToast } from '../../../../components/toastContext';
import { ApiError } from '../../../../lib/apiError';
import { addressPathLabel } from '../../../../lib/addressApi';
import { formatDateOnly, formatDateTime } from '../../../../lib/formatDate';
import { saveBlob } from '../../../../lib/saveBlob';
import { COMPETENCY_LEVEL_LABELS, type CompetencyLevel } from '../../../human-resources/personnel/personnelTypes';
import { surveyTrainingRecommendationsApi } from '../surveyApi';
import { TRAINING_DECISION, TRAINING_DECISION_LABELS, type SurveyTrainingRecommendationTrack } from '../surveyTypes';

const ID = 'training-track';

/**
 * One employee's track on a training recommendation, as Learning & Development reads it — their
 * answer (and why, if they declined), the training they took, and their report with its files.
 * What clicking a name on the recommendation's Personnel table opens.
 *
 * READ ONLY: every fact here is the EMPLOYEE's to write, from Self Service. So there is no primary
 * action, no tick and no Autogenerate — only Close. A file is FETCHED and saved, never linked, and
 * taking its bytes is its own key (`development-plans.download`).
 */
export function SurveyTrainingTrackModal({
  recommendationId,
  respondentId,
  employeeName,
  onClose,
}: {
  recommendationId: string;
  respondentId: string;
  employeeName: string;
  onClose: () => void;
}) {
  const toast = useToast();
  const [track, setTrack] = useState<SurveyTrainingRecommendationTrack | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [downloading, setDownloading] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    surveyTrainingRecommendationsApi
      .track(recommendationId, respondentId, controller.signal)
      .then((next) => {
        if (!controller.signal.aborted) setTrack(next);
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;

        setError(caught instanceof ApiError ? caught : new ApiError("Could not load this employee's training.", 0));
      });

    return () => controller.abort();
  }, [recommendationId, respondentId]);

  async function download(attachment: { id: string; fileName: string }) {
    setDownloading(attachment.id);

    try {
      const { blob, fileName } = await surveyTrainingRecommendationsApi.downloadTrackAttachment(recommendationId, respondentId, attachment.id);
      saveBlob(blob, fileName ?? attachment.fileName);
    } catch (caught) {
      toast.failure(caught instanceof ApiError ? caught.message : 'Could not download the attached file.');
    } finally {
      setDownloading(null);
    }
  }

  // `?.`: null until the track arrives.
  const startsOn = track?.trainingStartDate ?? track?.enrollmentStartDate;
  const endsOn = track?.trainingEndDate ?? track?.enrollmentEndDate;

  return (
    <Modal
      open
      onClose={onClose}
      title={employeeName}
      size="xl"
      id={`${ID}-modal`}
      footer={
        <Button id={`${ID}-close`} variant="secondary" onClick={onClose}>
          Close
        </Button>
      }
    >
      {error ? (
        <ErrorState message={error.message} correlationId={error.correlationId} />
      ) : !track ? (
        <div className="flex flex-col gap-3">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <PreviewTable
            variant="table-display"
            rows={[
              { label: 'Employee no.', value: track.employeeNumber, mono: true },
              { label: 'Answer', value: TRAINING_DECISION_LABELS[track.decision] },
              { label: 'Answered', value: track.decidedAt != null ? formatDateTime(track.decidedAt) : null, mono: true },
              ...(track.decision === TRAINING_DECISION.Declined ? [{ label: 'Reason', value: track.declineReason }] : []),
              { label: 'Enrolled', value: track.enrolledAt != null ? formatDateTime(track.enrolledAt) : null, mono: true },
              { label: 'Training school', value: track.trainingSchool },
              {
                label: 'Location',
                value:
                  addressPathLabel({ province: track.trainingProvince, cityMunicipality: track.trainingCityMunicipality, barangay: track.trainingBarangay }) ||
                  null,
              },
              { label: 'Street', value: track.trainingStreetAddress },
              // Calendar dates — `formatDateOnly`, never the instant path.
              // Once started, the dates Start training recorded; until then, the duration given at enrollment.
              { label: 'Training starts', value: startsOn != null ? formatDateOnly(startsOn) : null, mono: true },
              { label: 'Training ends', value: endsOn != null ? formatDateOnly(endsOn) : null, mono: true },
              { label: 'Why it was changed', value: track.trainingRemarks },
              {
                label: 'Result',
                value: track.trainingCompletedAt != null ? (track.trainingResult === 1 ? 'Cancelled' : 'Completed') : null,
              },
              { label: 'Ended', value: track.trainingCompletedAt != null ? formatDateTime(track.trainingCompletedAt) : null, mono: true },
              { label: 'Reason', value: track.trainingCompletionRemarks },
              { label: 'Report submitted', value: track.reportAt != null ? formatDateTime(track.reportAt) : null, mono: true },
              // The impact assessment — Learning & Development's, made after the report.
              { label: 'Result', value: track.impactPassed == null ? null : track.impactPassed ? 'Passed' : 'Failed' },
              {
                label: 'Level reached',
                value: track.impactAchievedLevel != null ? COMPETENCY_LEVEL_LABELS[track.impactAchievedLevel as CompetencyLevel] : null,
              },
              {
                label: 'Impact assessed',
                value: track.impactAssessmentAt != null ? formatDateTime(track.impactAssessmentAt) : null,
                mono: true,
              },
              { label: 'Impact assessed by', value: track.impactAssessmentByName },
            ]}
          />

          <section className="flex flex-col gap-2">
            <h3 className="text-sm font-medium text-slate-800">Attachments</h3>
            {track.reportAttachments.length === 0 ? (
              <p className="text-sm text-slate-400">—</p>
            ) : (
              <ul className="flex flex-col divide-y divide-slate-200 rounded-lg border border-slate-200">
                {track.reportAttachments.map((attachment) => (
                  <li key={attachment.id} className="flex items-center justify-between gap-3 px-3 py-2">
                    <span className="min-w-0 break-all text-sm text-slate-600">{attachment.fileName}</span>
                    {/* Seeing that a report has a file is the read; taking its bytes is its own key. */}
                    <PermissionGate permission="development-plans.download">
                      <Button
                        id={`${ID}-download-${attachment.id}`}
                        variant="secondary"
                        leading={<Download className="h-4 w-4" aria-hidden="true" />}
                        onClick={() => void download(attachment)}
                        loading={downloading === attachment.id}
                        loadingLabel="Downloading"
                      >
                        Download
                      </Button>
                    </PermissionGate>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {track.impactAssessmentAt != null ? (
            <section className="flex flex-col gap-2">
              <h3 className="text-sm font-medium text-slate-800">Impact assessment</h3>
              <RichTextDocument html={track.impactAssessment ?? null} />
            </section>
          ) : null}

          <section className="flex flex-col gap-2">
            <h3 className="text-sm font-medium text-slate-800">Report</h3>
            {/* The one renderer of stored markup — safe only because the server sanitised it on write. */}
            <RichTextDocument html={track.report ?? null} />
          </section>
        </div>
      )}
    </Modal>
  );
}
